const breakpoints = require('../services/breakpoints'),
	slotsStates = require('./slotStates'),
	adManager = require('../services/adManager'),
	sizeMappings = require('../services/sizeMappings'),
	ORIGINAL_ID_ATTRIBUTE = 'data-advert-orig-id',
	STATE_ATTRIBUTE = 'data-advert-slot-state',
	IS_EMPTY_ATTRIBUTE = 'data-advert-slot-empty';

class Slot {
	constructor(slotConfig, /** Settings */ settings) {
		this.name = slotConfig.name;
		this.domId = _generateDomId(this.name);
		this.adUnit = `/${settings.networkId}/${typeof slotConfig.adUnit === 'undefined' ? settings.adUnit : slotConfig.adUnit}`;
		this.sizeMapping = slotConfig.sizeMapping;
		this.targeting = Object.assign({
			'slot': this.name
		}, slotConfig.targeting || {});
		this.outOfPage = Boolean(slotConfig.outOfPage);
		this.lazyLoaded = Boolean(slotConfig.lazyLoad);
		this.refreshable = Boolean(slotConfig.refresh);
		this.lazyLoadThreshold = slotConfig.lazyLoadThreshold;
		this.refreshThreshold = slotConfig.refreshThreshold;
		this.refreshDelay = slotConfig.refreshDelay;
		this.refreshModes = slotConfig.refreshModes;
		this.node = null;
		this.renderBreakpoint = null;

		this._initGoogleTagProperties();

		this.updateState(slotsStates.CREATED);
	}

	_initGoogleTagProperties() {
		this.gtag = null;
		this.advertiserId = '';
		this.campaignId = '';
		this.creativeId = '';
		this.lineItemId = '';
		this.sourceAgnosticCreativeId = '';
		this.sourceAgnosticLineItemId = '';
		this.isEmpty = true;
		this.size = [];

		this.updateInViewPercentage(0);
		this.updateHasImpression(false);
	}

	updateState(newState) {
		this.state = newState;

		if (this.node) {
			this.node.setAttribute(STATE_ATTRIBUTE, this.state);
		}

		performance.mark(`advert - dfp - slot - ${this.name} - ${newState}`);

		window.advert.pubsub.publish(`slot.${newState}`, this);
		window.advert.pubsub.publish(`slot.${this.name}`, this);
		window.advert.pubsub.publish(`slot.${this.name}.${newState}`, this);
	}

	updateInViewPercentage(inViewPercentage) {
		this.inViewPercentage = Number(inViewPercentage);

		if (isNaN(this.inViewPercentage)) {
			this.inViewPercentage = 0;
		}

		window.advert.pubsub.publish('slot.inViewPercentageUpdated', this);
		window.advert.pubsub.publish(`slot.${this.name}.inViewPercentageUpdated`, this);
	}

	updateHasImpression(hasImpression) {
		this.hasImpression = Boolean(hasImpression);

		window.advert.pubsub.publish('slot.hasImpressionUpdated', this);
		window.advert.pubsub.publish(`slot.${this.name}.hasImpressionUpdated`, this);
	}

	updateRenderData(/** SlotRenderData */ renderData) {
		Object.assign(this, renderData);
		this.renderBreakpoint = breakpoints.getCurrentBreakpoint();

		if (this.node) {
			if (renderData.isEmpty) {
				this.node.setAttribute(IS_EMPTY_ATTRIBUTE, '');
			} else {
				this.node.removeAttribute(IS_EMPTY_ATTRIBUTE);
			}
		}
	}

	getAdUnit() {
		return this.adUnit
			.replace(new RegExp('%screenSize%', 'g'), breakpoints.getCurrentBreakpoint())
			.replace(new RegExp('%slotName%', 'g'), this.name);
	}

	getSizeMapping() {
		return sizeMappings.getSizeMapping(this.sizeMapping);
	}

	define() {
		return new Promise((resolve, reject) => {
			performance.mark(`advert - dfp - slot - ${this.name} - definining`);

			if (this.state !== slotsStates.CREATED) {
				const curState = this.state;

				this.updateState(slotsStates.ERROR);

				return reject(new Error(`slot.define - ${this.name} should be initialized first! State: ${curState}`));
			}

			adManager.defineSlot(this).then((gtag) => {
				this.gtag = gtag;
				this.updateState(slotsStates.DEFINED);

				resolve(this);
			}).catch(reject);
		});
	}

	load(node) {
		return new Promise((resolve, reject) => {
			performance.mark(`advert - dfp - slot - ${this.name} - loading`);

			if (this.state !== slotsStates.DEFINED) {
				const curState = this.state;

				this.updateState(slotsStates.ERROR);

				return reject(new Error(`slot.load - ${this.name} should be defined first! State: ${curState}`));
			}

			this.node = node;
			this.node.setAttribute(ORIGINAL_ID_ATTRIBUTE, this.node.id);
			this.node.id = this.domId;

			adManager.loadSlot(this).then(() => {
				this.updateState(slotsStates.LOADED);

				resolve(this);
			}).catch(reject);
		});
	}

	render() {
		return new Promise((resolve, reject) => {
			performance.mark(`advert - dfp - slot - ${this.name} - rendering`);

			if (this.state !== slotsStates.LOADED) {
				const curState = this.state;

				this.updateState(slotsStates.ERROR);

				return reject(new Error(`slot.render - ${this.name} should be loaded first! State: ${curState}`));
			}

			adManager.renderSlot(this).then((/** SlotRenderData */ renderData) => {
				this.updateRenderData(renderData);

				this.updateState(slotsStates.RENDERED);

				resolve(this);
			}).catch(reject);
		});
	}

	refresh(reason = 'other', changeCorrelator = true) {
		return new Promise((resolve, reject) => {
			performance.mark(`advert - dfp - slot - ${this.name} - refreshing`);

			if (this.state !== slotsStates.LOADED && this.state !== slotsStates.RENDERED) {
				const curState = this.state;

				this.updateState(slotsStates.ERROR);

				return reject(new Error(`slot.refresh - ${this.name} should be loaded first! State: ${curState}`));
			}

			this.updateInViewPercentage(0);
			this.updateHasImpression(false);
			this.targeting.refresh = reason;

			adManager.refreshSlots([this], changeCorrelator).then((/** SlotRenderData */ renderData) => {
				this.updateRenderData(renderData);

				this.updateState(slotsStates.RENDERED);

				resolve(this);
			}).catch(reject);
		});
	}

	reset() {
		return new Promise((resolve, reject) => {
			performance.mark(`advert - dfp - slot - ${this.name} - resetting`);

			adManager.destroySlot(this).then(() => {
				this.node.id = this.node.getAttribute(ORIGINAL_ID_ATTRIBUTE);
				this.node.removeAttribute(ORIGINAL_ID_ATTRIBUTE);
				this.node.removeAttribute(STATE_ATTRIBUTE);
				this.node.removeAttribute(IS_EMPTY_ATTRIBUTE);
				this.node = null;
				this.renderBreakpoint = null;

				this._initGoogleTagProperties();

				this.updateState(slotsStates.CREATED);

				resolve(this);
			}).catch(reject);
		});
	}
}

function _generateDomId(prefix) {
	return `${prefix}_${Math.round(Math.random() * 1e18)}`;
}

module.exports = Slot;
